// @ts-nocheck
import Clickable from "@insite/mobius/Clickable";
// import borderByState, { disabledByType, sizeVariantValues } from "@insite/mobius/FormField/formStyles"; // Starter Custom Code
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import combineTypographyProps from "@insite/mobius/utilities/combineTypographyProps";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import * as React from "react";
// import styled, { ThemeProps, withTheme } from "styled-components"; // Starter Custom Code

/* Starter Custom Code - Start */
import borderByState, { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import getColor from "@insite/mobius/utilities/getColor";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import constants from "@store/constants";
import { useValidateForm } from "@toolkit/Framework/Store/Toolkit/Components/Form/FormHooks";
import { disabledByType } from "@toolkit/Modules/mobius/FormField/formStyles";
import styled, { css, ThemeProps, withTheme } from "styled-components";

/* Store Custom Code - Start */
import AlertCircleFill from "@store/Library/Components/Toolkit/Icons/AlertCircle/AlertCircleFill";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
/* Store Custom Code - End */

const borderWidth = constants.formField.borderWidth;

export const RequiredWrapper: React.FC = () => {
    const requiredTextProps: TypographyPresentationProps = {
        css: css`
            color: ${getColor("danger")};
        `,
    };

    return <Typography {...requiredTextProps}> *</Typography>;
};
/* Starter Custom Code - End */

export type FormFieldSizeVariant = "default" | "small";
type LabelPosition = "top" | "left";

// A subset of form field props that could reasonably be used to determine theme css based on the value of the prop.
export interface FormFieldPropsMock {
    disabled?: boolean;
    error?: React.ReactNode;
    required?: boolean;
}

export interface FormFieldComponentProps {
    /** Disables the form field. */
    disabled?: boolean;
    /** Unique id to be passed into the description element. */
    descriptionId: number | string;
    /** Error message to be displayed below the input. */
    error?: React.ReactNode;
    /** The input to be styled, including icon if relevant.
     * @ignore */
    formInput: React.ReactNode;
    /** Hint text to be displayed below the input. */
    hint?: React.ReactNode;
    /**
     * Unique id to be passed into the input element.
     * If not provided, a random id is assigned (an id is required for accessibility purposes).
     */
    inputId: number | string;
    /** Label to be displayed above the input. */
    label?: React.ReactNode;
    /** Position of the label in relation to the form field. */
    labelPosition?: "top" | "left";
    /** Id to be passed into label. Can be used for accessibility for aria-labelledby. */
    labelId?: number | string;
    /** Adds an asterisk to the input's label (if provided). */
    required?: boolean;
    /** Is sometimes passed via a object spread */
    dispatch?: unknown;
    /** prevent ellipsis for "left" labelPosition default behaviour */
    preventEllipsis?: boolean;
    /** optional override for the required asterisk */
    requiredLabel?: string | React.ReactNode;
    // Starter Custom Code
    reactRef?: React.RefObject<HTMLDivElement | null>;
    /** Text to be passed to visually hidden component inside the label (for accessibility purposes). */
    labelAccessibleText?: string;
    // Store Custom Code
    /** Description text rendered between the label and form input. */
    labelDescription?: React.ReactNode;
    /** Props to be passed into the interstitial description Typography component. */
    labelDescriptionProps?: TypographyPresentationProps;
    /** Forces the form field to be disabled, overriding the `disabled` prop, this will bypass the DisablerContext. */
    forceDisabled?: boolean;
}

export interface FormFieldPresentationPropsCommon {
    /** Border type variants.
     * @themable */
    border?: "underline" | "rectangle" | "rounded";
    /** Props to be passed into the error message's Typography component.
     * @themable */
    errorProps?: TypographyPresentationProps;
    /** Props to be passed into the hint text's Typography component.
     * @themable */
    hintProps?: TypographyPresentationProps;
    /** Props to be passed into the label's Typography component.
     * @themable */
    labelProps?: TypographyPresentationProps;
    /** Size variants. Default is 40px tall, `small` is 30px tall.
     * @themable */
    sizeVariant?: FormFieldSizeVariant;
    /** The background color of the input.
     * @themable */
    backgroundColor?: string;
}

export interface FormFieldPresentationProps<T> extends FormFieldPresentationPropsCommon {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS strings or styled-components functions to be injected into nested components. These will override the theme defaults.
     * @themable */
    cssOverrides?: {
        formInputWrapper?: StyledProp<any>;
        descriptionWrapper?: StyledProp<any>;
        formField?: StyledProp<any>;
        inputSelect?: StyledProp<any>;
    };
}

export type FormFieldProps = FormFieldPresentationProps<FormFieldComponentProps> &
    FormFieldComponentProps &
    ThemeProps<BaseTheme>;

// below "as any" necessary to prevent deep type instantiation errors on `formStyles`
export const FormFieldIcon = styled(Icon)<IconPresentationProps>`
    /* Starter Custom Code - Start */
    pointer-events: none;
    color: inherit;
    justify-content: center;
    /* Starter Custom Code - End */
    ${injectCss}
`; // Starter Removed - as any
export const FormFieldClickable = styled(Clickable)<any>`
    /* Starter Removed - injectCss - Clickable already injects the css  */
`;

/* Starter Custom Code - Start */
export type FormFieldSizeVariantStyleProps = { _sizeVariant: FormFieldSizeVariant };
export type DescriptionWrapperStyleProps = FormFieldSizeVariantStyleProps & {
    id: string | number;
};
/* Starter Custom Code - End */

const DescriptionWrapper = styled.div<InjectableCss<any> & DescriptionWrapperStyleProps /* Starter Custom Code */>`
    display: flex;
    flex-direction: column;
    /* Starter Custom Code - Start */
    margin-top: ${({ _sizeVariant }) => sizeVariantValues[_sizeVariant].descriptionLineSpacing}px;
    /* Starter Custom Code - End */
    ${/* sc-selector */ TypographyStyle as any} + ${/* sc-selector */ TypographyStyle as any} {
        margin-top: ${({ _sizeVariant }) => sizeVariantValues[_sizeVariant].descriptionLineSpacing}px;
    }
    ${injectCss}
`;

type FormInputWrapperProps = {
    labelPosition?: LabelPosition;
    _sizeVariant: keyof typeof sizeVariantValues;
    _backgroundColor?: string;
};

export const FormInputWrapper = styled.div<Partial<InjectableCss<any>> & FormInputWrapperProps>`
    width: ${({ labelPosition }) => (labelPosition === "left" ? "auto" : "100%")}; /* Starter Custom Code */
    flex: 0 1 auto; /* Starter Custom Code */
    position: relative;
    ${/* sc-selector */ FormFieldIcon},
    ${FormFieldClickable as any} {
        position: absolute;
        box-sizing: border-box;
        /* Starter Custom Code - Start */
        top: 0;
        right: 0;
        width: ${({ _sizeVariant }) => sizeVariantValues[_sizeVariant].height}px;
        height: ${({ _sizeVariant }) => sizeVariantValues[_sizeVariant].height}px;

        &.icon-2 {
            right: ${x => sizeVariantValues[x._sizeVariant].height - borderWidth}px;
        }
        /* Starter Custom Code - End */
    }
    /* Starter Removed - FormFieldClickable */
    /* Starter Removed - FormFieldIcon */
    ${injectCss}
`;

type FormFieldLabelProps = InjectableCss<any> & {
    labelSize: FormFieldSizeVariant;
    labelPosition?: LabelPosition;
    // Starter Custom Code
    _size?: number | string;
};

export const FormFieldLabel = styled(Typography as any)<any /* FormFieldLabelProps */>`
    /* Starter Custom Code - Start */
    flex: 0 1 auto;
    align-self: flex-start;
    ${({ _size }) => (_size ? `font-size: ${typeof _size === "number" ? `${_size}px` : _size};` : null)}
    ${({ labelPosition, labelSize }: FormFieldLabelProps) => {
        if (labelPosition === "left") {
            return `padding: ${sizeVariantValues[labelSize].leftLabelPadding}px 0.9em 0 0;`;
        }
    }}
    /* Starter Custom Code - End */
    ${injectCss}
`;

/* Starter Custom Code - next line - export */
export interface FormFieldStyleProps
    extends InjectableCss<any>,
        ThemeProps<BaseTheme>,
        Required<Pick<FormFieldPresentationPropsCommon, "border">> {
    _sizeVariant: FormFieldSizeVariant;
    labelPosition?: LabelPosition;
    error?: React.ReactNode;
    inputSelectCss?: StyledProp<any>;
    _backgroundColor?: string;
}

export const FormFieldStyle = styled.div<FormFieldStyleProps>`
    margin: 0;
    padding: 0;
    border: 0;
    display: inline-flex;
    flex-direction: column;
    flex-direction: ${({ labelPosition }) => (labelPosition === "left" ? "row" : "column")};
    width: 100%;
    ${/* sc-selector */ FormFieldLabel} + ${/* sc-selector */ FormInputWrapper} {
        /* Starter Removed - DescriptionWrapper selector */
        margin-top: ${({ labelPosition, _sizeVariant }) =>
            labelPosition === "left" ? 0 : sizeVariantValues[_sizeVariant].descriptionLineSpacing}px;
    }

    /* Styling for the form element itself */
    input,
    select,
    .formField /* Starter Replaced - mobiusFileUpload */ {
        height: ${({ _sizeVariant }) => sizeVariantValues[_sizeVariant].height}px;
    }
    input:not(.react-datetime-picker__inputGroup__input),
    select:not(.react-datetime-picker__inputGroup__input),
    textarea,
    .formField /* Starter Replaced - mobiusFileUpload */
    /* Starter Removed - .react-datetime-picker */ {
        width: 100%;
        box-sizing: border-box;
        padding: 0 10px;
        font-family: inherit;
        font-size: ${({ _sizeVariant }) => sizeVariantValues[_sizeVariant].fontSize}px;
        outline: 0;
        appearance: none;
        color: inherit;
        background: ${({ _backgroundColor, theme }) => resolveColor(_backgroundColor, theme)};
        ${props => borderByState(props, "inactive")}
        &:disabled, &.disabled /* Starter Custom Code */ {
            ${props => disabledByType(props)};
            cursor: not-allowed;
        }
        /* Starter Custom Code - Start */
        ${props => {
            if (props.error) {
                return `
                    &:not(:disabled):not(.disabled) {
                        ${borderByState(props, "error")}
                        
                        &:focus, &:focus-within {
                            ${borderByState(props, "focus")}
                        }
                    }
                `;
            }
            return `
                &:focus, &:focus-within {
                    ${borderByState(props, "focus")}
                }
            `;
        }}
        ${({ _sizeVariant }) => {
            const { height } = sizeVariantValues[_sizeVariant];

            return `
                &.hasIcon { padding-right: ${height - borderWidth}px; }
                &.hasTwoIcon { padding-right: ${height * 2 - borderWidth}px; }
            `;
        }}
        /* Starter Custom Code - End */
        ${({ inputSelectCss }: any) => inputSelectCss}
    }
    input::-ms-clear,
    input::-ms-reveal {
        display: none;
    }
    textarea {
        /* Starter Custom Code - Start */
        vertical-align: top;
        min-height: ${({ _sizeVariant }) => sizeVariantValues[_sizeVariant].height}px;
        min-width: 100%;
        max-width: 100%;
        /* Starter Custom Code - End */
        ${(props: FormFieldStyleProps) => borderByState(props, "inactive", true)}
        ${(props: FormFieldStyleProps) => {
            if (props.error) {
                return `
                        &:not(:disabled) {
                            ${borderByState(props, "error", true)}
                        }
                    `;
            }
            return `
                    &:focus {
                        ${borderByState(props, "focus", true)}
                    }
                `;
        }}
    }
    ${injectCss}
`;

/**
 * Provides styles for form field components. Including `TextField`, `TextArea`, `DatePicker`, `FileUpload`, `Select`,
 * `TokenExFrame` & `DynamicDropdown`.
 */
const FormField: React.ComponentType<FormFieldProps> = ({
    disabled: baseDisabled, // Store Custom Code
    descriptionId,
    error,
    hint,
    formInput,
    inputId,
    label,
    labelId,
    labelPosition = "top",
    requiredLabel,
    preventEllipsis,
    required,
    mergeCss,
    reactRef, // Starter Custom Code
    labelAccessibleText, // Starter Custom Code
    labelDescription, // Store Custom Code
    labelDescriptionProps, // Store Custom Code
    forceDisabled, // Store Custom Code
    ...otherProps
}) => {
    /* Starter Custom Code - Start */
    const disabled = forceDisabled ?? baseDisabled; // Store Custom Code
    /* Starter Custom Code - End */
    const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(otherProps, {
        component: "textField",
        category: "formField",
    });
    const sizeVariant: FormFieldSizeVariant = applyProp("sizeVariant", "default");
    const typographyProps = combineTypographyProps({
        theme: otherProps.theme,
        passedProps: spreadProps("labelProps"),
        defaultProps: {
            ellipsis: labelPosition === "left" && !preventEllipsis,
            weight: 600,
            forwardAs: "label",
        },
    });
    const secondaryTextSize = sizeVariantValues[sizeVariant].fontSize;
    const resolvedMergeCss = mergeCss ?? otherProps.theme.formField.defaultProps?.mergeCss;

    useValidateForm(inputId, error); // Starter Custom Code

    let renderLabel;
    if (label === 0 || label) {
        renderLabel = (
            <FormFieldLabel
                {...typographyProps}
                _size={typographyProps.size || secondaryTextSize /* Starter Custom Code */}
                labelSize={sizeVariant}
                labelPosition={labelPosition}
                htmlFor={inputId}
                id={labelId || null}
                as={inputId ? typographyProps.forwardAs : "span" /* Starter Custom Code */}
                data-test-selector={`${(otherProps as any)["data-test-selector"]}-label`}
            >
                {label}
                {required && !disabled && (requiredLabel ?? <RequiredWrapper /* Starter Custom Code */ />)}
                {/* Starter Custom Code - Start */}
                {labelAccessibleText && <VisuallyHidden>{labelAccessibleText}</VisuallyHidden>}
                {/* Starter Custom Code - End */}
            </FormFieldLabel>
        );
    } else if (labelPosition === "left") {
        renderLabel = <div style={{ width: "140px" }} />;
    }

    let renderError;
    if (!disabled && /* Store Custom Code */ error !== true && (error === 0 || error)) {
        renderError = (
            <Typography
                {...combineTypographyProps({
                    theme: otherProps.theme,
                    passedProps: spreadProps("errorProps"),
                    defaultProps: {
                        size: secondaryTextSize,
                        color: "danger",
                        weight: 600,
                    },
                })}
                data-test-selector={`${(otherProps as any)["data-test-selector"]}-error`}
            >
                {/* Store Custom Code - Start */}
                <Icon
                    size={20}
                    src={AlertCircleFill}
                    color="danger"
                    css={css`
                        margin-right: ${pxToRem(5)};
                    `}
                />
                {/* Store Custom Code - End */}
                {error}
            </Typography>
        );
    }

    let renderHint;
    if (hint === 0 || hint) {
        renderHint = (
            <Typography
                {...combineTypographyProps({
                    theme: otherProps.theme,
                    passedProps: spreadProps("hintProps"),
                    defaultProps: {
                        size: secondaryTextSize,
                        color: "text.accent",
                    },
                })}
                data-test-selector="characters_Left"
            >
                {/* Store Custom Code - Start */}
                <Icon
                    size={20}
                    src={AlertCircleFill}
                    css={css`
                        margin-right: ${pxToRem(5)};
                    `}
                />
                {/* Store Custom Code - End */}
                {hint}
            </Typography>
        );
    }

    let description;
    if (renderError || renderHint) {
        description = (
            <DescriptionWrapper
                id={descriptionId as any}
                _sizeVariant={sizeVariant}
                css={applyStyledProp(["cssOverrides", "descriptionWrapper"], resolvedMergeCss)}
            >
                {renderError}
                {renderHint}
            </DescriptionWrapper>
        );
    }

    /* Store Custom Code - Start */
    let renderLabelDescription;
    if (labelDescription) {
        const { descriptionFontSize, descriptionLineHeight, descriptionLineSpacing } = sizeVariantValues[
            sizeVariant
        ] as any;

        renderLabelDescription = (
            <Typography
                {...combineTypographyProps({
                    theme: otherProps.theme,
                    passedProps: labelDescriptionProps,
                    defaultProps: {
                        color: "text.light",
                        css: css`
                            ${() => cssProp("font-size", descriptionFontSize, x => `${x}px`)}
                            ${() => cssProp("line-height", descriptionLineHeight)}
                            ${() => cssProp("margin-top", descriptionLineSpacing, x => (x >= 2 ? `${x - 2}px` : "0px"))}
                            ${() => cssProp("margin-bottom", descriptionLineSpacing, x => `${x}px`)}
                        `,
                    },
                })}
                data-test-selector="label-description"
            >
                {labelDescription}
            </Typography>
        );
    }
    /* Store Custom Code - End */

    return (
        <FormFieldStyle
            ref={reactRef /* Starter Custom Code */}
            border={applyProp("border", "underline")}
            _backgroundColor={applyProp("backgroundColor", "common.background")}
            css={applyStyledProp(["cssOverrides", "formField"], resolvedMergeCss)}
            inputSelectCss={applyStyledProp(["cssOverrides", "inputSelect"], resolvedMergeCss)}
            {...{
                disabled,
                error,
                _sizeVariant: sizeVariant,
                labelPosition,
            }}
        >
            {renderLabel}
            {/* Store Custom Code - Start */}
            {renderLabelDescription}
            {/* Store Custom Code - End */}
            <FormInputWrapper
                labelPosition={labelPosition}
                _sizeVariant={sizeVariant}
                css={applyStyledProp(["cssOverrides", "formInputWrapper"], resolvedMergeCss)}
                _backgroundColor={applyProp("backgroundColor")}
            >
                {formInput}
                {description}
            </FormInputWrapper>
        </FormFieldStyle>
    );
};

/** @component */
export default withTheme(FormField);
